/**
 * What the creature on the top bar is looking at, and where it belongs.
 *
 * One animal along the top bar, coloured by the worst thing currently true.
 * The rules are the platform's own signals and nothing else: red is failed or
 * out of touch, amber is waiting on the owner, green is ready. Everything in
 * this file is pure - the room passes its counts in - so both the reading and
 * the movement can be tested without a browser. The component only draws.
 */

export type WatchdogTone = 'calm' | 'asking' | 'danger';

/**
 * The four states the creature's look carries: nothing going on, work going
 * on, something waiting on the owner, something failed or out of touch. The
 * tone is the colour; the mood also splits calm into idle and working.
 */
export type WatchdogMood = 'idle' | 'working' | 'needs_you' | 'failed';

export interface WatchdogInput {
  /** Computers reporting in. */
  readonly connectedNodes: number;
  /** Computers the owner has connected at all. */
  readonly totalNodes: number;
  /** Tasks being worked on right now. */
  readonly running: number;
  /** Approvals waiting for the owner's decision. */
  readonly pendingApprovals: number;
  /** Workers that cannot go on until the owner answers them. */
  readonly blockedWorkers: number;
  /** Current failures: failed today, not superseded by a later success, not discarded. */
  readonly failedRuns: number;
}

export interface WatchdogReading {
  readonly tone: WatchdogTone;
  readonly mood: WatchdogMood;
  /** The one line that says what is true. It is shown in words, always. */
  readonly headline: string;
  /** The count underneath it. */
  readonly detail: string;
  /**
   * How much is waiting, as one number. It is not shown; the creature inks
   * when it RISES, which is what makes the ink mean "something new" rather
   * than "something is still open".
   */
  readonly attention: number;
  /** What the headline is about, so the failure headline can open the failure list. */
  readonly subject: 'out_of_touch' | 'failed' | 'waiting' | 'calm';
}

/**
 * The out-of-touch computers worth raising the alarm over.
 *
 * A viewer-only computer - one whose own reading says it hosts no Worker seats and
 * that no Worker lives on, like a tablet used to watch - takes no work, so its
 * being offline for days stops nothing. Its computer card still says it is out of
 * touch; it does not turn the top bar red. A computer with Worker seats, or one
 * whose seats are unknown, still does.
 */
export function outOfTouchWorkerHosts<T extends { readonly node_id: string }>(
  outOfTouch: readonly T[],
  evidence: {
    readonly capacity: readonly { readonly node_id: string; readonly worker_seats: number | null }[];
    readonly workerNodeIds: Iterable<string | null>;
  },
): T[] {
  const seats = new Map(evidence.capacity.map((row) => [row.node_id, row.worker_seats]));
  const hostsWorkers = new Set(evidence.workerNodeIds);
  return outOfTouch.filter((node) => hostsWorkers.has(node.node_id) || seats.get(node.node_id) !== 0);
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function watchdogState(input: WatchdogInput): WatchdogReading {
  const outOfTouch = Math.max(0, input.totalNodes - input.connectedNodes);
  const waiting = input.pendingApprovals + input.blockedWorkers;
  const attention = waiting + input.failedRuns + outOfTouch;

  const computers = input.totalNodes === 0
    ? 'no computers connected yet'
    : outOfTouch === 0
      ? `${plural(input.connectedNodes, 'computer', 'computers')} in touch`
      : `${input.connectedNodes} of ${input.totalNodes} computers in touch`;
  const work = input.running === 0 ? 'nothing running' : `${plural(input.running, 'task', 'tasks')} running`;
  const asked = waiting === 0 ? 'nothing waiting on you' : `${plural(waiting, 'thing', 'things')} waiting on you`;
  const detail = `${computers} · ${work} · ${asked}`;

  if (outOfTouch > 0) {
    return {
      tone: 'danger', mood: 'failed',
      headline: outOfTouch === 1 ? 'A computer is out of touch' : `${outOfTouch} computers are out of touch`,
      detail, attention, subject: 'out_of_touch',
    };
  }
  if (input.failedRuns > 0) {
    return {
      tone: 'danger', mood: 'failed',
      headline: input.failedRuns === 1 ? 'A task failed' : `${input.failedRuns} tasks failed`,
      detail, attention, subject: 'failed',
    };
  }
  if (waiting > 0) {
    return {
      tone: 'asking', mood: 'needs_you',
      headline: waiting === 1 ? 'Something is waiting on you' : `${waiting} things are waiting on you`,
      detail, attention, subject: 'waiting',
    };
  }
  return {
    tone: 'calm', mood: input.running > 0 ? 'working' : 'idle',
    headline: input.running > 0 ? 'Everything is moving' : 'All quiet',
    detail, attention, subject: 'calm',
  };
}

/* ------------------------------------------------------------------ *
 * Where it goes, and how it gets there.
 *
 * An octopus does not walk and it does not slide: it reaches an arm out,
 * takes hold, and hauls the body after it, then does it again. So travel is
 * described here as a whole number of PULLS, never as a distance and a
 * duration - the drawing then gets one arm cycle per pull and the body
 * surges once per pull instead of gliding. Movement is also purposeful: the
 * creature only ever moves because the reading changed, and it holds still
 * the rest of the time, which is what keeps it out of the way of reading.
 * ------------------------------------------------------------------ */

/** How far along the bar's free stretch the creature sits, 0 left, 1 right. */
export type Perch = number;

/** One pull carries it about a fifth of the stretch. */
export const STRIDE = 0.2;
/** Nearer than this and it is already where it belongs: it stays put. */
export const DEADBAND = 0.05;
/** How long one reach-grip-haul-release cycle takes. */
export const PULL_MS = 620;

/**
 * Where the creature belongs for a reading. Calm keeps it parked at the far
 * left, out of the way of everything the owner reads. The more there is to
 * answer for, the further right it hauls itself - towards the controls the
 * owner would use - so its POSITION carries meaning as well as its colour.
 */
export function perchFor(tone: WatchdogTone): Perch {
  if (tone === 'danger') return 0.96;
  if (tone === 'asking') return 0.46;
  return 0.04;
}

export interface CrawlDecision {
  /** Whether anything about its position should change at all. */
  readonly move: boolean;
  readonly direction: 'left' | 'right' | 'still';
  /** Whole arm-over-arm pulls. Zero means: be there, without crawling. */
  readonly strides: number;
  readonly durationMs: number;
}

const STILL: CrawlDecision = { move: false, direction: 'still', strides: 0, durationMs: 0 };

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Whether to crawl, which way, and in how many pulls.
 *
 * Small corrections are refused outright - a creature that twitched at every
 * refresh would be worse than useless on a screen someone is reading. With
 * motion turned down it still belongs in the new place, it simply arrives
 * there without the crawl.
 */
export function crawlDecision(input: { at: Perch; to: Perch; reducedMotion?: boolean }): CrawlDecision {
  const from = clamp(input.at);
  const to = clamp(input.to);
  const gap = to - from;
  if (Math.abs(gap) < DEADBAND) return STILL;
  const direction = gap > 0 ? 'right' : 'left';
  if (input.reducedMotion === true) return { move: true, direction, strides: 0, durationMs: 0 };
  const strides = Math.max(1, Math.round(Math.abs(gap) / STRIDE));
  return { move: true, direction, strides, durationMs: strides * PULL_MS };
}

/**
 * The body's own timing curve for a crawl, as a CSS easing.
 *
 * One surge per pull with a hold between, and a small overshoot at the end
 * where the mantle catches up and settles. This is the whole difference
 * between a creature hauling itself along and a picture being slid.
 */
export function pullEasing(strides: number): string {
  const pulls = Math.max(1, Math.round(strides));
  const points: string[] = ['0'];
  for (let k = 1; k <= pulls; k += 1) {
    const reached = k / pulls;
    const surgeEnd = ((k - 1) / pulls + 0.62 / pulls) * 100;
    const holdEnd = (k / pulls) * 100;
    const value = k === pulls ? '1.015' : reached.toFixed(3);
    points.push(`${value} ${surgeEnd.toFixed(1)}%`);
    if (k < pulls) points.push(`${reached.toFixed(3)} ${holdEnd.toFixed(1)}%`);
  }
  points.push('1');
  return `linear(${points.join(', ')})`;
}

/**
 * Whether the beak is on show.
 *
 * A real octopus's mouth is a small hard beak at the centre of the arm crown,
 * on the UNDERSIDE, and you only see it when the arms part. So it is shown
 * when the arms are parted and not otherwise: while it is hauling itself
 * along, and when it rears up over something that has gone wrong.
 */
export function beakShown(input: { crawling: boolean; tone: WatchdogTone }): boolean {
  return input.crawling || input.tone === 'danger';
}

/**
 * Whether the ink should fire. Only on the RISE: something new started
 * waiting. Something that merely stays open is not news and gets nothing.
 */
export function inkRises(before: number | null, now: number): boolean {
  return before !== null && now > before;
}

/* ------------------------------------------------------------------ *
 * What the creature says when it is clicked.
 *
 * A small bubble: what is running, what needs the owner, and the last run
 * that finished. Every entry is built from rows the room already holds -
 * nothing is read for it and nothing is guessed - and every entry names the
 * existing view it opens, so the room decides how to get there.
 * ------------------------------------------------------------------ */

/** The existing views and records a bubble entry can open. */
export type WatchdogLink =
  | { readonly kind: 'worker'; readonly workerId: string }
  | { readonly kind: 'result'; readonly workerId: string }
  | { readonly kind: 'reports' }
  | { readonly kind: 'attention' }
  | { readonly kind: 'plans' }
  | { readonly kind: 'failures' }
  | { readonly kind: 'computers' }
  | { readonly kind: 'history' };

export interface WatchdogBubbleEntry {
  readonly key: string;
  readonly label: string;
  readonly detail: string;
  readonly link: WatchdogLink;
}

export interface WatchdogBubble {
  readonly running: readonly WatchdogBubbleEntry[];
  /** Running tasks past the ones listed, opened through the Reports list. */
  readonly moreRunning: WatchdogBubbleEntry | null;
  readonly needsYou: readonly WatchdogBubbleEntry[];
  readonly lastFinished: WatchdogBubbleEntry | null;
}

export interface WatchdogBubbleInput {
  readonly running: readonly {
    readonly assignment_id: string; readonly title: string; readonly project_name: string;
    readonly worker_number: number | null; readonly worker_name: string | null;
  }[];
  readonly workers: readonly { readonly id: string; readonly number: number }[];
  readonly pendingApprovals: number;
  readonly plansWaiting: number;
  readonly blockedWorkers: number;
  readonly failedRuns: number;
  readonly outOfTouch: number;
  /** The newest finished run, or null when none is known. */
  readonly lastFinished: {
    readonly assignment_id: string; readonly title: string; readonly success: boolean;
    readonly worker_id: string | null; readonly worker: string;
  } | null;
}

/** How many running tasks the bubble lists before it says how many more. */
export const BUBBLE_RUNNING_MAX = 4;

export function watchdogBubble(input: WatchdogBubbleInput): WatchdogBubble {
  const listed = input.running.slice(0, BUBBLE_RUNNING_MAX);
  const running = listed.map((run): WatchdogBubbleEntry => {
    const worker = run.worker_number === null
      ? null
      : input.workers.find((w) => w.number === run.worker_number) ?? null;
    const who = run.worker_number === null
      ? (run.worker_name ?? 'Worker unknown')
      : run.worker_name === null ? `Worker ${run.worker_number}` : `Worker ${run.worker_number} · ${run.worker_name}`;
    return {
      key: `run-${run.assignment_id}`,
      label: run.title,
      detail: `${who} · ${run.project_name}`,
      link: worker === null ? { kind: 'reports' } : { kind: 'worker', workerId: worker.id },
    };
  });
  const extra = input.running.length - listed.length;
  const moreRunning: WatchdogBubbleEntry | null = extra > 0
    ? { key: 'run-more', label: `${extra} more running`, detail: 'Open the Reports list', link: { kind: 'reports' } }
    : null;

  const needsYou: WatchdogBubbleEntry[] = [];
  if (input.failedRuns > 0) {
    needsYou.push({
      key: 'failures', label: plural(input.failedRuns, 'task failed', 'tasks failed'),
      detail: 'Open the current failures', link: { kind: 'failures' },
    });
  }
  if (input.outOfTouch > 0) {
    needsYou.push({
      key: 'computers', label: `${plural(input.outOfTouch, 'computer', 'computers')} out of touch`,
      detail: 'Open the Engine Room', link: { kind: 'computers' },
    });
  }
  if (input.pendingApprovals > 0 || input.blockedWorkers > 0) {
    const held = input.blockedWorkers === 0 ? '' : ` · ${plural(input.blockedWorkers, 'Worker', 'Workers')} held until you answer`;
    needsYou.push({
      key: 'attention',
      label: input.pendingApprovals > 0 ? plural(input.pendingApprovals, 'approval waiting', 'approvals waiting') : 'Workers waiting on you',
      detail: `Open what needs you${held}`, link: { kind: 'attention' },
    });
  }
  if (input.plansWaiting > 0) {
    needsYou.push({
      key: 'plans', label: `${plural(input.plansWaiting, 'plan', 'plans')} to review`,
      detail: 'Open plan review', link: { kind: 'plans' },
    });
  }

  const last = input.lastFinished;
  const lastFinished: WatchdogBubbleEntry | null = last === null ? null : {
    key: `done-${last.assignment_id}`,
    label: last.title,
    detail: `${last.success ? 'Finished' : 'Failed'} · ${last.worker}`,
    link: last.worker_id === null ? { kind: 'history' } : { kind: 'result', workerId: last.worker_id },
  };

  return { running, moreRunning, needsYou, lastFinished };
}
